/**
 * Hidden "Server" setting of the native app: which Senvia POS server the app talks to.
 * Pure validation (unit-tested) + persistence in Capacitor Preferences.
 */
export type ServerUrlError = "invalid" | "https" | "path";
export type ServerUrlResult = { ok: true; url: string } | { ok: false; error: ServerUrlError };

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "10.0.2.2", "[::1]"]);

/** https only; plain http is accepted for localhost-style hosts when allowInsecureLocal (dev builds). Returns the origin. */
export function validateServerUrl(input: string, opts: { allowInsecureLocal?: boolean } = {}): ServerUrlResult {
  let s = (input ?? "").trim();
  if (!s || /\s/.test(s)) return { ok: false, error: "invalid" };
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) {
    if (/^[a-z][a-z0-9+.-]*:/i.test(s) && !/^[^:]+:\d+/.test(s)) return { ok: false, error: "https" }; // javascript:, ftp: …
    s = "https://" + s; // a bare host name means https
  }
  let u: URL;
  try { u = new URL(s); } catch { return { ok: false, error: "invalid" }; }
  if (u.protocol !== "https:" && u.protocol !== "http:") return { ok: false, error: "https" };
  if (u.username || u.password || (u.pathname !== "/" && u.pathname !== "") || u.search || u.hash || s.endsWith("?") || s.endsWith("#")) return { ok: false, error: "path" };
  const host = u.hostname.toLowerCase();
  const local = LOCAL_HOSTS.has(host);
  if (!local && !/^([a-z0-9-]+\.)+[a-z0-9-]{2,}$/.test(host) && !/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return { ok: false, error: "invalid" };
  if (u.protocol === "http:" && !(local && opts.allowInsecureLocal)) return { ok: false, error: "https" };
  if (u.protocol === "https:" && local && !opts.allowInsecureLocal) return { ok: false, error: "https" }; // localhost = this tablet itself
  return { ok: true, url: u.origin };
}

const KEY = "spos.serverUrl";
export async function loadServerUrl(): Promise<string | null> {
  const { Preferences } = await import("@capacitor/preferences");
  return (await Preferences.get({ key: KEY })).value;
}
export async function saveServerUrl(url: string | null): Promise<void> {
  const { Preferences } = await import("@capacitor/preferences");
  if (url) await Preferences.set({ key: KEY, value: url }); else await Preferences.remove({ key: KEY });
}

/** Asks GET /health on the candidate server; true when it answers like a Senvia POS API. */
export async function probeServer(url: string, fetchImpl: typeof fetch = fetch): Promise<boolean> {
  try {
    const r = await fetchImpl(url + "/api/health", { headers: { accept: "application/json" }, signal: AbortSignal.timeout(8000) });
    if (!r.ok) return false;
    const j = (await r.json()) as { service?: string };
    return j.service === "salon-pos-api";
  } catch { return false; }
}
